import { DateTime } from 'luxon';
import * as React from 'react';
import styled from 'styled-components';

import { addDataEvent } from '@creditornot/browser-telemetry';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Spinner } from '@creditornot/cb-progress';
import { WoltGoodsReceipt } from 'common/api-types/warehouse';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { SearchInput } from 'common/components/cookbook/Search/SearchInput';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useInView } from 'common/hooks/useInView';
import { isNotNullDateTimeInvariant } from 'common/utils/invariant';
import { InfiniteScrollLoadingMore } from 'features/warehouse/common/components/InfiniteScrollLoadingMore';
import { WarehousePageApiError } from 'features/warehouse/common/components/WarehouseApiErrorNotification';

import { DayRow } from './DayRow';
import { GoodsReceiptRow } from './GoodsReceiptRow';
import { useGoodsReceiptsInfiniteQuery } from '../../query';

const PlaceholderContainer = styled(ContentContainer)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchInputContainer = styled(ContentContainer)`
  border-bottom: 1px solid ${color.border};
`;

const isToday = (isoString: string) => {
  const dateTime = DateTime.fromISO(isoString);

  return dateTime.toISODate() === DateTime.now().toISODate();
};

const isYesterday = (isoString: string) => {
  const dateTime = DateTime.fromISO(isoString);

  return dateTime.toISODate() === DateTime.now().plus({ day: -1 }).toISODate();
};

const groupGoodsReceiptsByDay = (goodsReceipts: WoltGoodsReceipt[]) => {
  return goodsReceipts.reduce(
    (acc, goodsReceipt) => {
      const createdToday = isToday(goodsReceipt.createdAt);
      const createdYesterday = isYesterday(goodsReceipt.createdAt);

      if (createdToday) {
        return {
          ...acc,
          today: [...acc.today, goodsReceipt],
        };
      }

      if (createdYesterday) {
        return {
          ...acc,
          yesterday: [...acc.yesterday, goodsReceipt],
        };
      }

      return acc;
    },
    { today: [], yesterday: [] } as { today: WoltGoodsReceipt[]; yesterday: WoltGoodsReceipt[] },
  );
};

export const RecentlyAddedItems: React.FC<{ selectedWarehouseId: string }> = ({
  selectedWarehouseId,
}) => {
  const formatMessage = useFormatMessage();

  const [searchQuery, setSearchQuery] = React.useState('');

  const { data, isLoading, isFetching, error, hasNextPage, fetchNextPage } =
    useGoodsReceiptsInfiniteQuery(selectedWarehouseId, {
      q: searchQuery || undefined,
      from: isNotNullDateTimeInvariant(DateTime.now().plus({ day: -1 }).toISODate()),
      embed: 'INBOUND_LOADS',
    });

  const { ref } = useInView({
    onInView: () => {
      if (hasNextPage) {
        void fetchNextPage();
      }
    },
  });

  const goodsReceipts = React.useMemo(
    () => data?.pages?.flatMap((page) => page.items) || [],
    [data],
  );

  React.useEffect(() => {
    if (searchQuery) {
      addDataEvent('recently_added_items', 'search_results', {
        results_count: goodsReceipts.length,
        wm_venue_id: selectedWarehouseId,
        search_value: searchQuery,
      });
    }
  }, [searchQuery, selectedWarehouseId, goodsReceipts.length]);

  const groupedGoodsReceipts = React.useMemo(
    () => groupGoodsReceiptsByDay(goodsReceipts),
    [goodsReceipts],
  );

  return (
    <>
      <SearchInputContainer>
        <SearchInput
          value={searchQuery}
          onChange={(value) => {
            setSearchQuery(value);
          }}
          placeholder={formatMessage('receiving.search_items')}
        />
      </SearchInputContainer>
      {isLoading ? (
        <PlaceholderContainer>
          <Spinner size="large" />
        </PlaceholderContainer>
      ) : error ? (
        <WarehousePageApiError error={error} />
      ) : goodsReceipts?.length === 0 && searchQuery.length === 0 ? (
        <PlaceholderContainer>
          {formatMessage('receiving.no_recently_added_items')}
        </PlaceholderContainer>
      ) : goodsReceipts?.length === 0 && searchQuery.length > 0 ? (
        <PlaceholderContainer>{formatMessage('receiving.no_items_found')}</PlaceholderContainer>
      ) : (
        <>
          {!!groupedGoodsReceipts.today.length && <DayRow variant="today" />}
          {groupedGoodsReceipts.today.map((goodsReceipt, index) => {
            const { id: goodsReceiptId, location, quantity, product, inboundLoad } = goodsReceipt;
            const { name, gtin, image } = product;

            return (
              <GoodsReceiptRow
                key={goodsReceiptId}
                productName={name}
                productGtin={gtin}
                productImage={image}
                quantity={quantity}
                locationName={location?.name}
                inboundLoadIdentifier={inboundLoad?.publicId}
                ref={index === groupedGoodsReceipts.today.length - 10 ? ref : null}
              />
            );
          })}
          {!!groupedGoodsReceipts.yesterday.length && <DayRow variant="yesterday" />}
          {groupedGoodsReceipts.yesterday.map((goodsReceipt, index) => {
            const {
              id: goodsReceiptId,
              location,
              quantity,
              product,
              inboundLoad,
              inboundLoadComment,
            } = goodsReceipt;
            const { name, gtin, image } = product;

            return (
              <GoodsReceiptRow
                key={goodsReceiptId}
                productName={name}
                productGtin={gtin}
                productImage={image}
                quantity={quantity}
                locationName={location?.name}
                inboundLoadIdentifier={inboundLoad?.publicId || inboundLoadComment}
                ref={index === groupedGoodsReceipts.yesterday.length - 10 ? ref : null}
              />
            );
          })}
          {!isLoading && isFetching && <InfiniteScrollLoadingMore size="small" />}
        </>
      )}
    </>
  );
};
